import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { DenaliScopeFilter } from '../DenaliAnalysis/filters/DenaliScopeFilter'
import useBlueprintPresentation from './hooks/useBlueprintPresentation'

/**
 * Adapter that bridges QBPScopeProvider's scope state into the
 * DenaliFilterContext consumed by DA components.
 *
 * Reads the Scope Selector presentation config from a blueprint, derives
 * a column map from the referenced purpose's scopePredicates, and wraps
 * children in DenaliScopeFilter. DA components nested inside will receive
 * `membershipFilter` via `useDenaliFilter()` without manual wiring.
 *
 * NOTE: this bridge only applies to `mode: 'membership'` scope selectors.
 * For other modes (`value-source`, `mixed`, `purpose`) the bridge falls
 * back to defaults — value-source / mixed scopes are not bridged to legacy
 * DA components and those components should be migrated to QBP renderers
 * if they need scope.
 *
 * Must be rendered inside a QBPScopeProvider (or any DenaliScopeProvider).
 */
function QBPScopeFilterBridge ({ blueprintCode, presentationKey, defaultColumn, children }) {
  const { presentationConfig, definition } = useBlueprintPresentation(blueprintCode, presentationKey)

  const config = presentationConfig?.config

  const { column, scopeColumnMap } = useMemo(() => {
    if (!config || !definition || config.mode !== 'membership') {
      return { column: defaultColumn ?? 'clientId', scopeColumnMap: {} }
    }

    const purposeData = definition.data?.purposes?.[config.purposeCode]
    const predicates = (purposeData?.scopePredicates ?? [])
      .filter(p => p.type === 'membership')

    const map = predicates.reduce((acc, pred) => {
      acc[pred.scopeType] = pred.column
      return acc
    }, {})

    const fallbackColumn = defaultColumn ?? predicates[0]?.column ?? 'clientId'

    return { column: fallbackColumn, scopeColumnMap: map }
  }, [config, definition, defaultColumn])

  return (
    <DenaliScopeFilter column={column} scopes={scopeColumnMap}>
      {children}
    </DenaliScopeFilter>
  )
}

QBPScopeFilterBridge.propTypes = {
  /** Blueprint code to load the scope selector config from */
  blueprintCode: PropTypes.string.isRequired,

  /** Presentation key that holds the Scope Selector config */
  presentationKey: PropTypes.string.isRequired,

  /** Fallback column when no scope predicates are available */
  defaultColumn: PropTypes.string,

  children: PropTypes.node
}

export default QBPScopeFilterBridge
